import { Box, Radio, Text, TextFieldSize, ToastType } from '@bechtel/gds';
import { Cable, CableEnd, MetadataTableName, OperatorType, UiTextEnum } from '@bechtel/sr-types';
import { FieldArray, setNestedObjectValues, useFormikContext } from 'formik';
import { defaultTo, find, includes, isEmpty, isNil, map } from 'lodash';
import { FC, useEffect } from 'react';
import {
  CommodityFooter,
  DateFieldComponent,
  FormField,
  FormSectionHeading,
  MultiLingualText,
  SelectFieldComponent,
  TableMetadataContext,
  TextFieldComponent,
  useAppUser,
} from '~/components';
import { useDebounce, usePermissions, useSelectListQuery, useTableMetadataQuery, useToast } from '~/hooks';
import {
  CableMarkHeaderStyled,
  FeedbackCardStyled,
  FeedbackMainContainerStyled,
  FormContainerStyled,
} from '../../styled';
import { getMarksData, terminationFeedbackInitialValues } from '../utils';
import {
  CableMarkFromFieldStyled,
  CableMarkFromHeaderStyled,
  CableMarkRowContainerStyled,
  CableMarkRowStyled,
  CableMarkToFieldStyled,
  OptionContainerStyled,
} from './styled';
import { TerminationFeedbackFormikState, TerminationFormProps } from './types';
const SelectedItemComponent = (items) => {
  return <Text>{items.item.column2}</Text>;
};
const ItemComponent = (items) => {
  return <Text>{items.item.column2}</Text>;
};
const getInitialValues = (cableData: Cable, commodityType: CableEnd): Partial<TerminationFeedbackFormikState> => {
  let initialValues: Partial<TerminationFeedbackFormikState>;
  if (commodityType === CableEnd.FROM) {
    initialValues = {
      termConstStatus: isNil(cableData.fromTermConstStatus) ? '' : cableData.fromTermConstStatus,
      termConnMade: isNil(cableData.fromTermConnMade) ? 0 : cableData.fromTermConnMade,
      termCrimpTool: isNil(cableData.fromTermCrimpTool) ? '' : cableData.fromTermCrimpTool,
      termInstalledBy: isNil(cableData.fromTermInstalledBy) ? '' : cableData.fromTermInstalledBy,
      termInstalledTime: isNil(cableData.fromTermInstalledTime) ? '' : cableData.fromTermInstalledTime,
      termInspectedBy: isNil(cableData.fromTermInspectedBy) ? '' : cableData.fromTermInspectedBy,
      termInspectedTime: isNil(cableData.fromTermInspectedTime) ? '' : cableData.fromTermInspectedTime,
      commodityType: commodityType,
    };
  } else {
    initialValues = {
      termConstStatus: isNil(cableData.toTermConstStatus) ? '' : cableData.toTermConstStatus,
      termConnMade: isNil(cableData.toTermConnMade) ? 0 : cableData.toTermConnMade,
      termCrimpTool: isNil(cableData.toTermCrimpTool) ? '' : cableData.toTermCrimpTool,
      termInstalledBy: isNil(cableData.toTermInstalledBy) ? '' : cableData.toTermInstalledBy,
      termInstalledTime: isNil(cableData.toTermInstalledTime) ? '' : cableData.toTermInstalledTime,
      termInspectedBy: isNil(cableData.toTermInspectedBy) ? '' : cableData.toTermInspectedBy,
      termInspectedTime: isNil(cableData.toTermInspectedTime) ? '' : cableData.toTermInspectedTime,
      commodityType: commodityType,
    };
  }
  return initialValues;
};
const TerminationFeedbackForm: FC<TerminationFormProps> = ({ searchCables, loading, cableData, setInitialValues }) => {
  const { showToast } = useToast();
  const {
    setFieldValue,
    values,
    handleSubmit,
    handleReset,
    setFieldTouched,
    dirty,
    touched,
    isSubmitting,
    validateForm,
    setTouched,
  } = useFormikContext<TerminationFeedbackFormikState>();
  const loadingCheck = loading || isEmpty(values.tag);
  const [getSelectListData, { data }] = useSelectListQuery('CABLE.FROM_TERM_CONST_STATUS');
  useEffect(() => {
    getSelectListData();
  }, [getSelectListData]);
  const appUser = useAppUser();
  const [getTableMetaData, { data: tableMetadata }] = useTableMetadataQuery();
  useEffect(() => {
    if (!appUser.userAccount?.defaultProject?.id) return;
    getTableMetaData({
      variables: {
        name: MetadataTableName.CABLE_MARK,
        projectId: appUser.userAccount?.defaultProject?.id,
      },
    });
  }, [appUser.userAccount?.defaultProject?.id, getTableMetaData]);
  useEffect(() => {
    if (!cableData) return;
    const feedbackValues: Partial<TerminationFeedbackFormikState> = {
      ...terminationFeedbackInitialValues,
      id: cableData?.id,
      tag: cableData?.tag,
      marks: getMarksData(cableData),
    };
    if (isNil(values.commodityType) || !includes([CableEnd.FROM, CableEnd.TO], values.commodityType)) {
      setInitialValues({ ...feedbackValues });
      return;
    }
    if (values.commodityType === CableEnd.FROM) {
      setInitialValues({ ...feedbackValues, ...getInitialValues(cableData, CableEnd.FROM) });
      return;
    }
    if (values.commodityType === CableEnd.TO) {
      setInitialValues({ ...feedbackValues, ...getInitialValues(cableData, CableEnd.TO) });
    }
  }, [cableData, setInitialValues, values.commodityType]);
  const { canUpdateTerminationFeedback } = usePermissions();
  const debouncedTag = useDebounce(values.tag, 400);
  useEffect(() => {
    if (!debouncedTag) return;
    searchCables({
      variables: {
        searchInput: {
          filterInput: {
            and: {
              field: 'tag',
              operator: OperatorType.EQUAL,
              value: debouncedTag,
            },
          },
        },
      },
      onCompleted: (data: { searchCables: { items: Cable[] } }) => {
        if (data?.searchCables?.items?.length) {
          setFieldValue('tagInvalid', false);
        } else {
          setFieldValue('tagInvalid', true);
        }
      },
    });
  }, [debouncedTag, searchCables, setFieldValue]);
  const defaultSelectedItem = defaultTo(
    find(data?.getSelectListData?.items, { column1: values.termConstStatus }),
    values.termConstStatus
  );

  const handleSave = async () => {
    const formErrors = await validateForm();
    if (formErrors) {
      if (Object.keys(formErrors).length > 0) {
        setTouched(setNestedObjectValues(formErrors, true));
        showToast(UiTextEnum.ONE_OR_MORE_FIELDS_ARE_INVALID, [''], ToastType.WARNING);
        return;
      }
    }
    handleSubmit();
  };
  return (
    <>
      <FeedbackMainContainerStyled>
        <FeedbackCardStyled>
          <FormContainerStyled>
            <Box mb={{ sm: '450' }}>
              <FormField
                isMainCommodity={true}
                memberName="tag"
                required={true}
                pickListName="TERMINATION_FEEDBACK"
                commodityName={`commodities/cable`}
                displayMemberId="id"
                hasFieldPicklist={true}
              >
                <TextFieldComponent />
              </FormField>
            </Box>
            <Box css={{ gap: '72px' }} mb={{ sm: '700' }}>
              <Box css={{ width: '50%' }} flexDirection={{ sm: 'column' }}>
                <TableMetadataContext metadataTableName={MetadataTableName.CABLE_MARK}>
                  <FormField
                    alwaysEditable={canUpdateTerminationFeedback}
                    memberName="commodityType"
                    metadataMemberName="cableEnd"
                    required={true}
                  >
                    <TextFieldComponent />
                  </FormField>
                </TableMetadataContext>
                <Box mt={{ sm: '400' }}>
                  <MultiLingualText size="200" color="gray.300" textKey="ENTER_F_OR_T"></MultiLingualText>
                </Box>
              </Box>
              <Box css={{ width: '50%' }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
                <Box alignItems={{ sm: 'center' }} mb={{ sm: '300' }}>
                  <Box>
                    <Radio
                      name="equipment"
                      size={TextFieldSize.MEDIUM}
                      id="from-equipment"
                      checked={values.commodityType === CableEnd.FROM}
                      onClick={() => {
                        if (!canUpdateTerminationFeedback) return;
                        setFieldValue('commodityType', CableEnd.FROM);
                      }}
                    />
                  </Box>
                  <OptionContainerStyled>
                    {cableData?.cableView?.fromEquipmentTag ? (
                      <Text
                        as="label"
                        size="300"
                        color="gray.900"
                        title={cableData?.cableView?.fromEquipmentTag}
                        /* @ts-expect-error GDS typing need to updated*/
                        htmlFor="from-equipment"
                        style={{ textOverflow: 'ellipsis' }}
                      >
                        {cableData?.cableView?.fromEquipmentTag}
                      </Text>
                    ) : (
                      /* @ts-expect-error GDS typing need to updated*/
                      <MultiLingualText size="300" color="gray.900" htmlFor="from-equipment">
                        FROM End
                      </MultiLingualText>
                    )}
                  </OptionContainerStyled>
                </Box>
                <Box alignItems={{ sm: 'center' }}>
                  <Box>
                    <Radio
                      name="equipment"
                      size={TextFieldSize.MEDIUM}
                      id="to-equipment"
                      checked={values.commodityType === CableEnd.TO}
                      onClick={() => {
                        if (!canUpdateTerminationFeedback) return;
                        setFieldValue('commodityType', CableEnd.TO);
                      }}
                    />
                  </Box>
                  <OptionContainerStyled>
                    {cableData?.cableView?.toEquipmentTag ? (
                      <Text
                        as="label"
                        size="300"
                        color="gray.900"
                        style={{ textOverflow: 'ellipsis' }}
                        title={cableData?.cableView?.toEquipmentTag}
                        /* @ts-expect-error GDS typing need to updated*/
                        htmlFor="to-equipment"
                      >
                        {cableData?.cableView?.toEquipmentTag}
                      </Text>
                    ) : (
                      /* @ts-expect-error GDS typing need to updated*/
                      <MultiLingualText size="300" color="gray.900" htmlFor="to-equipment">
                        TO End
                      </MultiLingualText>
                    )}
                  </OptionContainerStyled>
                </Box>
              </Box>
            </Box>
            <FormSectionHeading heading="Status" />
            <Box mb={{ sm: '700' }}>
              <FormField
                alwaysEditable={!loadingCheck && canUpdateTerminationFeedback}
                metadataMemberName="constructionStatus"
                memberName="termConstStatus"
                required={true}
                showPicker={false}
                readOnly={loadingCheck}
                validate={false}
              >
                <SelectFieldComponent
                  items={data?.getSelectListData?.items}
                  selectedComponent={SelectedItemComponent}
                  ItemComponent={ItemComponent}
                  onSelectedItemChange={(changes) => {
                    setFieldValue('termConstStatus', changes.selectedItem.column1);
                  }}
                  selectedItem={defaultSelectedItem}
                />
              </FormField>
            </Box>
            <FormSectionHeading heading="Installation" />
            <Box flexDirection={{ sm: 'column' }} mb={{ sm: '700' }}>
              <Box css={{ gap: '72px' }} mb={{ sm: '450' }}>
                <Box css={{ width: '50%' }}>
                  <FormField
                    alwaysEditable={!loadingCheck && canUpdateTerminationFeedback}
                    metadataMemberName={values.commodityType === CableEnd.FROM ? 'fromTermConnMade' : 'toTermConnMade'}
                    memberName="termConnMade"
                    required={true}
                  >
                    <TextFieldComponent />
                  </FormField>
                </Box>
                <Box css={{ width: '50%' }}>
                  <FormField
                    alwaysEditable={!loadingCheck && canUpdateTerminationFeedback}
                    metadataMemberName={
                      values.commodityType === CableEnd.FROM ? 'fromTermCrimpTool' : 'toTermCrimpTool'
                    }
                    memberName="termCrimpTool"
                  >
                    <TextFieldComponent />
                  </FormField>
                </Box>
              </Box>
              <Box css={{ gap: '72px' }}>
                <Box css={{ width: '50%' }}>
                  <FormField
                    alwaysEditable={!loadingCheck && canUpdateTerminationFeedback}
                    metadataMemberName={
                      values.commodityType === CableEnd.FROM ? 'fromTermInstalledBy' : 'toTermInstalledBy'
                    }
                    memberName="termInstalledBy"
                  >
                    <TextFieldComponent />
                  </FormField>
                </Box>
                <Box css={{ width: '50%' }}>
                  <FormField
                    alwaysEditable={!loadingCheck && canUpdateTerminationFeedback}
                    metadataMemberName={
                      values.commodityType === CableEnd.FROM ? 'fromTermInstalledTime' : 'toTermInstalledTime'
                    }
                    memberName="termInstalledTime"
                  >
                    <DateFieldComponent
                      selected={values.termInstalledTime && new Date(values.termInstalledTime)}
                      onChange={(date) => {
                        setFieldValue('termInstalledTime', date);
                        setFieldTouched('termInstalledTime', true, false);
                      }}
                      showPopperArrow={false}
                      size={TextFieldSize.SMALL}
                      icon="calendar"
                      iconStyle="regular"
                    />
                  </FormField>
                </Box>
              </Box>
            </Box>
            <FormSectionHeading heading="Inspection" />
            <Box css={{ gap: '72px' }} mb={{ sm: '700' }}>
              <Box css={{ width: '50%' }}>
                <FormField
                  alwaysEditable={!loadingCheck && canUpdateTerminationFeedback}
                  metadataMemberName={
                    values.commodityType === CableEnd.FROM ? 'fromTermInspectedBy' : 'toTermInspectedBy'
                  }
                  memberName="termInspectedBy"
                >
                  <TextFieldComponent />
                </FormField>
              </Box>
              <Box css={{ width: '50%' }}>
                <FormField
                  alwaysEditable={!loadingCheck && canUpdateTerminationFeedback}
                  metadataMemberName={
                    values.commodityType === CableEnd.FROM ? 'fromTermInspectedTime' : 'toTermInspectedTime'
                  }
                  memberName="termInspectedTime"
                >
                  <DateFieldComponent
                    selected={values.termInspectedTime && new Date(values.termInspectedTime)}
                    onChange={(date) => {
                      setFieldValue('termInspectedTime', date);
                      setFieldTouched('termInspectedTime', true, false);
                    }}
                    showPopperArrow={false}
                    size={TextFieldSize.SMALL}
                    icon="calendar"
                    iconStyle="regular"
                  />
                </FormField>
              </Box>
            </Box>
            <FormSectionHeading tableMetadata={tableMetadata?.tableMetadata} />
            <Box css={{ gap: '72px' }}>
              <Box mb={{ sm: '700' }} flexDirection={{ sm: 'column' }} style={{ width: '50%' }}>
                <CableMarkHeaderStyled>
                  <CableMarkFromHeaderStyled css={{ width: '50%' }} p={{ sm: '400' }}>
                    <MultiLingualText size="300" color="gray.900" weight="600">
                      FROM Mark
                    </MultiLingualText>
                  </CableMarkFromHeaderStyled>
                  <Box css={{ width: '50%' }} p={{ sm: '400' }}>
                    <MultiLingualText size="300" color="gray.900" weight="600">
                      TO Mark
                    </MultiLingualText>
                  </Box>
                </CableMarkHeaderStyled>
                <CableMarkRowContainerStyled flexDirection={{ sm: 'column' }} flexGrow={{ sm: 1 }}>
                  <TableMetadataContext metadataTableName={MetadataTableName.CABLE_MARK}>
                    <FieldArray
                      name="marks"
                      render={() => (
                        <>
                          {values.marks &&
                            values.marks.length > 0 &&
                            map(values.marks, ({ sequenceNumber }, index) => (
                              <CableMarkRowStyled key={sequenceNumber} flexGrow={{ sm: 1 }}>
                                <CableMarkFromFieldStyled flexBasis={{ sm: '50%' }}>
                                  <FormField
                                    memberName={`marks.${index}.fromMark`}
                                    alwaysEditable={
                                      !(loadingCheck || values.commodityType !== CableEnd.FROM) &&
                                      canUpdateTerminationFeedback
                                    }
                                    metadataMemberName="mark"
                                  >
                                    <TextFieldComponent />
                                  </FormField>
                                </CableMarkFromFieldStyled>
                                <CableMarkToFieldStyled flexBasis={{ sm: '50%' }}>
                                  <FormField
                                    memberName={`marks.${index}.toMark`}
                                    alwaysEditable={
                                      !(loadingCheck || values.commodityType !== CableEnd.TO) &&
                                      canUpdateTerminationFeedback
                                    }
                                    metadataMemberName="mark"
                                  >
                                    <TextFieldComponent />
                                  </FormField>
                                </CableMarkToFieldStyled>
                              </CableMarkRowStyled>
                            ))}
                        </>
                      )}
                    />
                  </TableMetadataContext>
                </CableMarkRowContainerStyled>
              </Box>
              <Box style={{ width: '50%' }}></Box>
            </Box>
          </FormContainerStyled>
        </FeedbackCardStyled>
      </FeedbackMainContainerStyled>
      <CommodityFooter
        saveHandler={handleSave}
        loading={isSubmitting}
        disableSave={!canUpdateTerminationFeedback || !dirty || !touched}
        disableCancel={!dirty || !touched}
        cancelHandler={() => handleReset()}
      />
    </>
  );
};
export default TerminationFeedbackForm;
